import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { EmptyState, LoadingState } from '@/components/ui/states'
import { usePublicSchedule } from '@/hooks/usePublicSchedule'
import { PublicShell, PublicProblem } from '@/components/public/PublicShell'
import { formatEventDate, timeRangeLabel } from '@/lib/eventTime'

/**
 * A venue's next two weeks, for whoever holds the link.
 *
 * No event names, by design — the rows are a date, a time and the spaces in use,
 * which is what tells two of them apart and is all a crew needs to pick the one
 * they are here for. See `docs/public-schedule.md`.
 */
export function PublicSchedulePage(): React.ReactElement {
  const { token } = useParams()
  const shareToken = token ?? ''
  const query = usePublicSchedule(shareToken)

  if (query.isPending) {
    return (
      <PublicShell>
        <LoadingState label="Loading the schedule…" />
      </PublicShell>
    )
  }

  if (query.isError) return <PublicProblem error={query.error} />

  const { venueName, windowDays, events } = query.data

  return (
    <PublicShell venueName={venueName}>
      <div className="space-y-1">
        <h1 className="m-0 text-2xl font-semibold text-ink">Setup schedule</h1>
        <p className="m-0 text-sm text-ink-muted">
          The next {windowDays} days at {venueName}, and yesterday for anything still to clear.
          Open a day to see its spaces, plans and instructions.
        </p>
      </div>

      {events.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title={`Nothing booked in the next ${String(windowDays)} days`}
          description="Nothing from yesterday is left to clear either. This page updates on its own — check back rather than asking for a new link."
        />
      ) : (
        <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface p-0">
          {events.map((event) => {
            const times = timeRangeLabel(event.startsAt, event.endsAt)
            return (
              <li key={event.id}>
                <Link
                  to={`/s/${shareToken}/${event.id}`}
                  className="flex flex-col gap-1 p-4 outline-none transition-colors hover:bg-row-hover focus-visible:focus-ds"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-medium text-ink">
                      {formatEventDate(event.date)}
                    </span>
                    {/* A past date on a list of upcoming ones reads as upcoming.
                        Saying what it is there for is the whole reason the window
                        reaches back at all. */}
                    {event.isPast ? (
                      <Badge tone="neutral">Finished — for teardown</Badge>
                    ) : null}
                  </span>
                  <span className="text-sm text-ink-muted">
                    {/* The time is data: the mono face. */}
                    <span className="font-mono tabular-nums">{times === '' ? 'Time not set' : times}</span>
                    {event.spaces.length > 0 ? ` · ${event.spaces.join(', ')}` : ''}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </PublicShell>
  )
}
